/**
 * GlobalView — Satellite Icons
 *
 * Canvas-drawn satellite pictograms for the Space Layer.
 * Provides different icons for:
 *   - General satellite (body + solar panels)
 *   - Space station (ISS/CSS — larger, distinctive)
 *   - Starlink (simpler, smaller)
 *
 * Icons are cached per color for efficient reuse.
 */

// ─── Config ──────────────────────────────────────────────────────

const ICON_SIZE = 32;
const HALF = ICON_SIZE / 2;

// ─── Cache ───────────────────────────────────────────────────────

const iconCache = new Map<string, HTMLCanvasElement>();

// ─── Drawing Helpers ─────────────────────────────────────────────

/**
 * Draw a general satellite: central body + two solar panels.
 *
 *   ┌──┐     ┌──┐
 *   │  │─────│  │
 *   │  │  ◆  │  │
 *   │  │─────│  │
 *   └──┘     └──┘
 */
function drawSatellite(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.save();
  ctx.translate(HALF, HALF);

  // Solar panels (two rectangles)
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.7;

  // Left panel
  ctx.fillRect(-14, -5, 8, 10);
  // Right panel
  ctx.fillRect(6, -5, 8, 10);

  // Panel grid lines
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.4;
  ctx.lineWidth = 0.5;
  // Left panel grid
  ctx.strokeRect(-14, -5, 4, 10);
  ctx.strokeRect(-10, -5, 4, 10);
  // Right panel grid
  ctx.strokeRect(6, -5, 4, 10);
  ctx.strokeRect(10, -5, 4, 10);

  // Panel struts (connecting lines)
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-6, 0);
  ctx.lineTo(-4, 0);
  ctx.moveTo(4, 0);
  ctx.lineTo(6, 0);
  ctx.stroke();

  // Central body (diamond/rhombus)
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -4);
  ctx.lineTo(4, 0);
  ctx.lineTo(0, 4);
  ctx.lineTo(-4, 0);
  ctx.closePath();
  ctx.fill();

  // Body highlight
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(0, -4);
  ctx.lineTo(2, -1);
  ctx.lineTo(0, 2);
  ctx.lineTo(-2, -1);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

/**
 * Draw a space station icon: larger body with distinctive shape.
 */
function drawStation(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.save();
  ctx.translate(HALF, HALF);

  // Large solar arrays
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.8;

  // Left array
  ctx.fillRect(-15, -7, 10, 14);
  // Right array
  ctx.fillRect(5, -7, 10, 14);

  // Array grid
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 0.5;
  for (let x = -15; x <= 10; x += 5) {
    if (x >= -5 && x < 5) continue; // skip body area
    ctx.strokeRect(x, -7, 5, 7);
    ctx.strokeRect(x, 0, 5, 7);
  }

  // Truss (horizontal beam)
  ctx.globalAlpha = 0.8;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.moveTo(-15, 0);
  ctx.lineTo(15, 0);
  ctx.stroke();

  // Central module (cylinder-like)
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  ctx.fillRect(-4, -3, 8, 6);

  // Module detail
  ctx.globalAlpha = 0.4;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-3, -2, 6, 2);

  ctx.restore();
}

/**
 * Draw a Starlink satellite: simple flat panel shape.
 */
function drawStarlink(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.save();
  ctx.translate(HALF, HALF);

  // Single large solar panel
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.7;
  ctx.fillRect(-10, -3, 20, 6);

  // Panel grid
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 0.5;
  for (let x = -10; x < 10; x += 5) {
    ctx.strokeRect(x, -3, 5, 6);
  }

  // Body dot
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, 2, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ─── Public API ──────────────────────────────────────────────────

export type SatelliteIconType = 'satellite' | 'station' | 'starlink';

/**
 * Get a satellite icon as a Canvas element.
 * Cached by type + color for efficiency.
 */
export function getSatelliteIconCanvas(
  type: SatelliteIconType,
  color: string,
): HTMLCanvasElement {
  const key = `${type}:${color}`;
  const cached = iconCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext('2d')!;

  switch (type) {
    case 'station':
      drawStation(ctx, color);
      break;
    case 'starlink':
      drawStarlink(ctx, color);
      break;
    default:
      drawSatellite(ctx, color);
      break;
  }

  iconCache.set(key, canvas);
  return canvas;
}

/**
 * Determine the icon type for a satellite based on its properties.
 */
export function getSatelliteIconType(
  name: string,
  constellation?: string,
): SatelliteIconType {
  if (constellation === 'Starlink') return 'starlink';
  const upper = name.toUpperCase();
  if (upper.includes('ISS') || upper.includes('CSS') || upper.includes('TIANHE')
    || upper.includes('ZARYA') || upper.includes('NAUKA') || upper.includes('WENTIAN')
    || upper.includes('MENGTIAN') || upper.includes('SHENZHOU') || upper.includes('SOYUZ')
    || upper.includes('PROGRESS') || upper.includes('CYGNUS') || upper.includes('HTV')
    || upper.includes('TIANGONG') || upper.includes('TIANZHOU')) {
    return 'station';
  }
  return 'satellite';
}
